/**
 * ComposeEditor — the rich-text (HTML) body editor for the email composer.
 *
 * A TipTap editor (StarterKit + underline/link/placeholder plus the Group-D rich
 * extensions: headings, text alignment, colour, font family, tables and inline
 * images) with a formatting toolbar. The editor owns the body content: it reports
 * `(html, text)` out via `onChange` for the send payload and validation, and
 * exposes an imperative handle so the composer can seed it (reply / forward /
 * draft resume), replace it (template / AI draft), insert at the cursor (snippet),
 * or append (signature).
 *
 * Outgoing-HTML safety is the TipTap schema itself: only the registered nodes and
 * marks survive serialization, so script/style/event attributes from a pasted,
 * template-sourced, or quoted-original fragment are dropped on parse — we never
 * ship raw third-party HTML out through the composer.
 *
 * Inline images travel as CID parts, not base64: on insert the bytes are uploaded
 * to the Vault (via `onUploadImage`) and the node carries a `data-doc-id`; the
 * editor shows a local data-URI preview only. The composer rewrites each
 * `data-doc-id` image to `src="cid:<documentId>"` at send time and attaches the
 * Vault bytes as an inline part, so the wire HTML (and the persisted Sent copy)
 * stay small and render through the same `cid:` path as received mail.
 */
import { forwardRef, useImperativeHandle, useReducer, useRef, useState } from 'react';
import { EditorContent, type Editor } from '@tiptap/react';
import { useRichTextEditor } from '@/lib/rich-text-editor';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, Color, FontFamily } from '@tiptap/extension-text-style';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Link2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ImageIcon,
  Images as ImagesIcon,
  Table as TableIcon,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/** Cap inline images so a huge paste doesn't blow the 1 MB send-HTML budget or
 *  the reader's 5 MB inline-render threshold. Mirrors EmailHtmlBody. */
export const MAX_INLINE_IMAGE_BYTES = 5 * 1024 * 1024;

// Inline table styling for the OUTGOING html. The editor's own borders come from
// scoped CSS (below), which doesn't travel with the email — mail clients strip
// <style>/external CSS — so the sent table looked border-less. Emit the borders
// as inline styles on the table/cells so both our reader pane and external
// clients render the grid. Injected via each extension's `HTMLAttributes`, which
// TipTap concatenates onto any existing style (e.g. column widths), so this
// preserves colwidth rather than clobbering it.
// `width:100%` so an un-resized table fills the body (mail clients otherwise
// shrink a style-less table to its content — the "minimal width columns" the
// user saw); explicit per-column widths (below) still override this per cell.
const TABLE_HTML_STYLE = 'border-collapse:collapse;width:100%;';
const TABLE_CELL_HTML_STYLE = 'border:1px solid #d1d5db;padding:6px 8px;vertical-align:top;';
const TABLE_HEADER_HTML_STYLE = `${TABLE_CELL_HTML_STYLE}background:#f3f4f6;font-weight:600;text-align:left;`;

const FONT_FAMILIES: { label: string; value: string }[] = [
  { label: 'Default font', value: '' },
  { label: 'Sans-serif', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Serif', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Monospace', value: '"Courier New", monospace' },
];

export interface ComposeEditorHandle {
  /** Replace the entire body (template apply, AI draft, reply/forward seed). */
  setHtml(html: string): void;
  /** Insert at the current cursor position (snippet). */
  insertHtml(html: string): void;
  /** Append at the end of the document (signature). */
  appendHtml(html: string): void;
  /**
   * Swap the trailing auto-signature for `html` (or remove it when `html` is
   * null) — used when the From account changes on a fresh compose. Locates the
   * last `-- ` delimiter paragraph and replaces from there to the end of the
   * document, so the user's body above the signature is untouched.
   */
  replaceSignature(html: string | null): void;
  /** Focus the editor. `'start'` puts the cursor at the top (reply above quote). */
  focus(position?: 'start' | 'end'): void;
}

interface ComposeEditorProps {
  initialHtml: string;
  onChange: (html: string, text: string) => void;
  placeholder?: string;
  /**
   * Upload an inline image to the Vault, resolving to its document id. When
   * absent (e.g. no upload holder resolved yet) the "from computer" control is
   * hidden.
   */
  onUploadImage?: (file: File) => Promise<string>;
  /**
   * Pick an existing image from the Vault ("Fias filesystem"), resolving to its
   * document id + a preview URL — or null if the user cancels. When absent the
   * "from Fias" control is hidden.
   */
  onPickVaultImage?: () => Promise<{ documentId: string; url: string } | null>;
  /**
   * Fired once the underlying editor is created and its imperative handle can
   * accept content. The composer waits for this before auto-appending the
   * signature — otherwise the append raced editor init and silently no-op'd.
   */
  onReady?: () => void;
}

// Append inline table borders to the serialized HTML (see TABLE_*_HTML_STYLE).
// The stock extensions render border-less <table>/<td>/<th>, so the sent grid
// looked edge-less in mail clients (which strip <style>). Spreading HTMLAttributes
// keeps colspan/rowspan/colwidth; we only append the trailing border style.
const appendStyle = (attrs: Record<string, unknown>, style: string) => {
  const existing = typeof attrs.style === 'string' && attrs.style ? `${attrs.style};` : '';
  return { ...attrs, style: `${existing}${style}` };
};
// TipTap's `resizable` table stores each cell's column width in the `colwidth`
// attr (px per spanned column). Mail clients strip the <colgroup> the editor's
// node-view renders, so fold the width into an inline `width:` on the cell — the
// only column-width signal that survives to the recipient. Returns '' when the
// column wasn't resized (falls back to the table's even 100% distribution).
function colwidthStyle(node: { attrs?: Record<string, unknown> } | undefined): string {
  const cw = node?.attrs?.colwidth;
  if (!Array.isArray(cw)) return '';
  const total = cw.reduce((sum: number, w) => sum + (Number(w) || 0), 0);
  return total > 0 ? `width:${total}px;` : '';
}
const StyledTable = Table.extend({
  renderHTML({ HTMLAttributes }) {
    return ['table', appendStyle(HTMLAttributes, TABLE_HTML_STYLE), ['tbody', 0]];
  },
});
const StyledTableHeader = TableHeader.extend({
  renderHTML({ node, HTMLAttributes }) {
    return [
      'th',
      appendStyle(HTMLAttributes, `${TABLE_HEADER_HTML_STYLE}${colwidthStyle(node)}`),
      0,
    ];
  },
});
const StyledTableCell = TableCell.extend({
  renderHTML({ node, HTMLAttributes }) {
    return ['td', appendStyle(HTMLAttributes, `${TABLE_CELL_HTML_STYLE}${colwidthStyle(node)}`), 0];
  },
});

// The Image node carries `data-doc-id` so the composer can map each inline image
// back to its Vault document at send time (rewrite src → cid, attach bytes).
const InlineImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-doc-id': {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-doc-id'),
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs['data-doc-id'] ? { 'data-doc-id': attrs['data-doc-id'] as string } : {},
      },
    };
  },
});

// Scoped under .compose-editor so it can't leak into the rest of the app
// (mirrors the chat composer's .chat-composer scoping).
const EDITOR_STYLES = `
.compose-editor .ProseMirror {
  outline: none;
  min-height: 14rem;
  /* No max-height/overflow here: the editor grows with its content and the
     composer's single outer scroll container handles overflow, so the message
     body doesn't get its own nested scrollbar inside the dialog. */
  padding: 8px 12px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}
.compose-editor .ProseMirror > * + * { margin-top: 6px; }
.compose-editor .ProseMirror p { margin: 0; }
.compose-editor .ProseMirror h1 { font-size: 1.5rem; font-weight: 700; line-height: 1.25; }
.compose-editor .ProseMirror h2 { font-size: 1.25rem; font-weight: 700; line-height: 1.3; }
.compose-editor .ProseMirror h3 { font-size: 1.1rem; font-weight: 600; line-height: 1.35; }
.compose-editor .ProseMirror ul,
.compose-editor .ProseMirror ol { padding-left: 1.5rem; margin: 6px 0; }
.compose-editor .ProseMirror ul { list-style: disc; }
.compose-editor .ProseMirror ol { list-style: decimal; }
.compose-editor .ProseMirror li { margin: 2px 0; }
.compose-editor .ProseMirror a {
  color: hsl(var(--primary));
  text-decoration: underline;
  cursor: pointer;
}
.compose-editor .ProseMirror img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}
.compose-editor .ProseMirror img.ProseMirror-selectednode {
  outline: 2px solid hsl(var(--primary));
}
.compose-editor .ProseMirror blockquote {
  border-left: 3px solid hsl(var(--border));
  padding-left: 0.75rem;
  color: hsl(var(--muted-foreground));
  margin: 6px 0;
}
.compose-editor .ProseMirror table {
  border-collapse: collapse;
  margin: 8px 0;
  width: auto;
  overflow: hidden;
  table-layout: fixed;
}
.compose-editor .ProseMirror td,
.compose-editor .ProseMirror th {
  border: 1px solid hsl(var(--border));
  padding: 4px 8px;
  vertical-align: top;
  min-width: 3rem;
  position: relative;
}
.compose-editor .ProseMirror th {
  background: hsl(var(--muted));
  font-weight: 600;
  text-align: left;
}
.compose-editor .ProseMirror .selectedCell::after {
  content: '';
  position: absolute;
  inset: 0;
  background: hsl(var(--primary) / 0.12);
  pointer-events: none;
}
.compose-editor .ProseMirror .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background: hsl(var(--primary));
  cursor: col-resize;
}
.compose-editor .ProseMirror p.is-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: hsl(var(--muted-foreground) / 0.6);
  pointer-events: none;
  height: 0;
}
`;

/**
 * Toolbar button — onMouseDown preventDefault keeps the editor's selection
 * intact while the formatting control is clicked.
 */
function ToolbarBtn({
  active,
  disabled,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded transition-colors',
        'text-muted-foreground hover:bg-muted hover:text-foreground',
        'disabled:pointer-events-none disabled:opacity-40',
        active && 'bg-primary/10 text-primary',
      )}
    >
      {children}
    </button>
  );
}

const Divider = () => <div className="mx-0.5 h-4 w-px shrink-0 bg-border" />;

/**
 * Compact text control for the table row/column ops — lucide lacks a clear
 * per-axis add/delete glyph, so short labels ("+Col" / "−Row") read
 * unambiguously where two near-identical grid icons would not.
 */
function TableCtl({
  label,
  title,
  onClick,
}: {
  label: string;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex h-7 items-center justify-center rounded px-1.5 text-xs font-medium tabular-nums transition-colors',
        'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

/** The block-type (paragraph vs H1–H3) picker. */
function headingValue(editor: Editor): string {
  for (const level of [1, 2, 3] as const) {
    if (editor.isActive('heading', { level })) return `h${level}`;
  }
  return 'p';
}

export const ComposeEditor = forwardRef<ComposeEditorHandle, ComposeEditorProps>(
  function ComposeEditor(
    { initialHtml, onChange, placeholder, onUploadImage, onPickVaultImage, onReady },
    ref,
  ) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [imageBusy, setImageBusy] = useState(false);
    const [imageError, setImageError] = useState<string | null>(null);
    // Re-render the toolbar on editor transactions (see onSelectionUpdate below).
    const [, bumpToolbar] = useReducer((n: number) => n + 1, 0);

    // Created in the mount effect, never during render (see useRichTextEditor),
    // so `createdEditor` is null on the first render.
    const createdEditor = useRichTextEditor({
      extensions: [
        StarterKit,
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer nofollow' },
        }),
        Placeholder.configure({ placeholder: placeholder ?? 'Write your message…' }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        TextStyle,
        Color,
        FontFamily,
        InlineImage.configure({ allowBase64: true }),
        StyledTable.configure({ resizable: true }),
        TableRow,
        StyledTableHeader,
        StyledTableCell,
      ],
      content: initialHtml,
      // Seed the payload mirror from the (normalized) initial content, then keep
      // it current on every edit.
      onCreate: ({ editor: e }) => {
        onChange(e.getHTML(), e.getText());
        onReady?.();
      },
      onUpdate: ({ editor: e }) => {
        onChange(e.getHTML(), e.getText());
        bumpToolbar();
      },
      // TipTap v3's useEditor does NOT re-render the component on selection-only
      // changes, so the toolbar's active/contextual state (bold highlight, and
      // especially the table row/column controls) went stale — clicking back into
      // a table left its options hidden. Force a re-render on every selection.
      onSelectionUpdate: () => bumpToolbar(),
    });
    // A destroyed instance (the hook tears one down on unmount and on a deps
    // change) throws on `commands`, so treat it as "not ready yet". (Same guard
    // as the chat composer, MessageInput.)
    const editor = createdEditor && !createdEditor.isDestroyed ? createdEditor : null;

    useImperativeHandle(
      ref,
      () => ({
        setHtml(html: string) {
          if (!editor) return;
          editor.commands.setContent(html);
          // setContent doesn't reliably emit an update; mirror the payload now.
          onChange(editor.getHTML(), editor.getText());
        },
        insertHtml(html: string) {
          editor?.chain().focus().insertContent(html).run();
        },
        appendHtml(html: string) {
          editor?.chain().focus('end').insertContent(html).run();
        },
        replaceSignature(html: string | null) {
          if (!editor) return;
          const { doc } = editor.state;
          // Find the LAST top-level block that is the "-- " signature delimiter.
          let delimiterPos: number | null = null;
          doc.forEach((node, offset) => {
            if (node.isTextblock && node.textContent.trim() === '--') delimiterPos = offset;
          });
          let chain = editor.chain();
          if (delimiterPos !== null) {
            chain = chain.deleteRange({ from: delimiterPos, to: doc.content.size });
          }
          if (html) {
            chain = chain.focus('end').insertContent(`<p>-- </p>${html}`);
          }
          chain.run();
          onChange(editor.getHTML(), editor.getText());
        },
        focus(position?: 'start' | 'end') {
          editor?.commands.focus(position ?? null);
        },
      }),
      [editor, onChange],
    );

    if (!editor) return null;

    const setLink = () => {
      const prev = (editor.getAttributes('link').href as string | undefined) ?? '';
      const url = window.prompt('Link URL', prev || 'https://');
      if (url === null) return;
      if (url.trim() === '') {
        editor.chain().focus().unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    };

    const setBlockType = (value: string) => {
      if (value === 'p') {
        editor.chain().focus().setParagraph().run();
        return;
      }
      const level = Number(value.slice(1)) as 1 | 2 | 3;
      editor.chain().focus().toggleHeading({ level }).run();
    };

    const setFont = (value: string) => {
      if (value === '') editor.chain().focus().unsetFontFamily().run();
      else editor.chain().focus().setFontFamily(value).run();
    };

    const pickImage = () => {
      setImageError(null);
      fileInputRef.current?.click();
    };

    const onImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset so re-selecting the same file re-triggers change.
      e.target.value = '';
      if (!file || !onUploadImage) return;
      if (!file.type.startsWith('image/')) {
        setImageError('Only image files can be inserted inline.');
        return;
      }
      if (file.size > MAX_INLINE_IMAGE_BYTES) {
        setImageError('Image is too large (max 5 MB).');
        return;
      }
      setImageBusy(true);
      setImageError(null);
      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(String(fr.result));
          fr.onerror = () => reject(new Error('read failed'));
          fr.readAsDataURL(file);
        });
        const documentId = await onUploadImage(file);
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'image',
            attrs: { src: dataUrl, alt: file.name, 'data-doc-id': documentId },
          })
          .run();
      } catch {
        setImageError('Image upload failed. Please try again.');
      } finally {
        setImageBusy(false);
      }
    };

    // Insert an image the user already has in the Vault ("Fias filesystem"). The
    // doc already exists, so we insert its preview URL + data-doc-id directly (no
    // upload); send-time CID rewrite treats it the same as an uploaded image.
    const insertVaultImage = async () => {
      if (!onPickVaultImage) return;
      setImageError(null);
      setImageBusy(true);
      try {
        const picked = await onPickVaultImage();
        if (picked?.url) {
          editor
            .chain()
            .focus()
            .insertContent({
              type: 'image',
              attrs: { src: picked.url, 'data-doc-id': picked.documentId },
            })
            .run();
        }
      } catch {
        setImageError('Could not insert the image from Fias.');
      } finally {
        setImageBusy(false);
      }
    };

    const inTable = editor.isActive('table');

    return (
      <div className="compose-editor space-y-1">
        <style dangerouslySetInnerHTML={{ __html: EDITOR_STYLES }} />
        <div className="flex flex-wrap items-center gap-0.5 rounded-md border bg-muted/40 p-1">
          <select
            aria-label="Text style"
            title="Text style"
            value={headingValue(editor)}
            onChange={(e) => setBlockType(e.target.value)}
            className="h-7 rounded border-0 bg-transparent px-1 text-xs text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="p">Normal</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>
          <select
            aria-label="Font"
            title="Font"
            value={(editor.getAttributes('textStyle').fontFamily as string | undefined) ?? ''}
            onChange={(e) => setFont(e.target.value)}
            className="h-7 max-w-[7.5rem] rounded border-0 bg-transparent px-1 text-xs text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {FONT_FAMILIES.map((f) => (
              <option key={f.label} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <Divider />
          <ToolbarBtn
            title="Bold"
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Italic"
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Underline"
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Strikethrough"
            active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <Strikethrough className="h-4 w-4" />
          </ToolbarBtn>
          <label
            title="Text colour"
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded hover:bg-muted"
          >
            <span
              className="text-sm font-semibold leading-none"
              style={{
                color: (editor.getAttributes('textStyle').color as string) || 'currentColor',
              }}
            >
              A
            </span>
            <input
              type="color"
              aria-label="Text colour"
              value={(editor.getAttributes('textStyle').color as string) || '#000000'}
              onInput={(e) =>
                editor
                  .chain()
                  .focus()
                  .setColor((e.target as HTMLInputElement).value)
                  .run()
              }
              className="sr-only"
            />
          </label>
          <Divider />
          <ToolbarBtn
            title="Align left"
            active={editor.isActive({ textAlign: 'left' })}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <AlignLeft className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Align center"
            active={editor.isActive({ textAlign: 'center' })}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <AlignCenter className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Align right"
            active={editor.isActive({ textAlign: 'right' })}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <AlignRight className="h-4 w-4" />
          </ToolbarBtn>
          <Divider />
          <ToolbarBtn
            title="Bulleted list"
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Numbered list"
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Quote"
            active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <Quote className="h-4 w-4" />
          </ToolbarBtn>
          <Divider />
          <ToolbarBtn title="Link" active={editor.isActive('link')} onClick={setLink}>
            <Link2 className="h-4 w-4" />
          </ToolbarBtn>
          {onUploadImage && (
            <ToolbarBtn title="Insert image from computer" disabled={imageBusy} onClick={pickImage}>
              <ImageIcon className="h-4 w-4" />
            </ToolbarBtn>
          )}
          {onPickVaultImage && (
            <ToolbarBtn
              title="Insert image from Fias"
              disabled={imageBusy}
              onClick={insertVaultImage}
            >
              <ImagesIcon className="h-4 w-4" />
            </ToolbarBtn>
          )}
          <ToolbarBtn
            title="Insert table"
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
          >
            <TableIcon className="h-4 w-4" />
          </ToolbarBtn>
          {inTable && (
            <>
              <Divider />
              <TableCtl
                label="+Col"
                title="Add column"
                onClick={() => editor.chain().focus().addColumnAfter().run()}
              />
              <TableCtl
                label="−Col"
                title="Delete column"
                onClick={() => editor.chain().focus().deleteColumn().run()}
              />
              <TableCtl
                label="+Row"
                title="Add row"
                onClick={() => editor.chain().focus().addRowAfter().run()}
              />
              <TableCtl
                label="−Row"
                title="Delete row"
                onClick={() => editor.chain().focus().deleteRow().run()}
              />
              <ToolbarBtn
                title="Delete table"
                onClick={() => editor.chain().focus().deleteTable().run()}
              >
                <Trash2 className="h-4 w-4" />
              </ToolbarBtn>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onImageFile}
          />
        </div>
        {imageError && <p className="px-1 text-xs text-destructive">{imageError}</p>}
        <div className="rounded-md border bg-background focus-within:ring-1 focus-within:ring-ring">
          <EditorContent editor={editor} />
        </div>
      </div>
    );
  },
);
